// External libraries
import { Component, createRef } from "react";

// Custom PeoplePicker component
import { CustomPeoplePicker } from "../../Common/UI/CustomPeoplePicker";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Input } from "@progress/kendo-react-inputs";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { Else, If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class PeoplePickerField extends Component {
  inputRef = createRef();

  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: JSON.tryParse(value, ""),
      valid: this.validate(value, properties?.IsRequired),
      invisible: false,
      disabled: false,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.selectionMode = properties?.SelectionMode ?? "single";
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.label = properties?.Label || `${fieldName}:`;
    this.labelVisible = labelVisible;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A person is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      JSON.stringify(this.state.value) !== JSON.stringify(nextState.value) ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched
    );
  }

  componentDidUpdate() {
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    return isRequired ? validators.person(value) : true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value && entryValues.value[this.fieldName] !== "") {
        this.clearField();
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect = () => {
    this.disposeDependencyEffect = effect(() => {
      let selectedPeople = [];
      if (typeof this.eventIndex === "number") {
        selectedPeople =
          entryValues.value?.[this.galleryParentName]?.[this.eventIndex]?.[
            this.fieldName.split("_")?.[0]
          ] ?? "";
      } else {
        selectedPeople = entryValues.value[this.fieldName];
      }
      let parsedSelectedPeople = [];
      if (typeof selectedPeople === "string") {
        if (selectedPeople.trim() !== "") {
          parsedSelectedPeople = JSON.tryParse(selectedPeople, []);
        }
      } else {
        parsedSelectedPeople = selectedPeople;
      }

      if (Array.isArray(parsedSelectedPeople) && parsedSelectedPeople.length === 0) {
        parsedSelectedPeople = "";
      }

      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
        value: parsedSelectedPeople,
      });
    });
  };

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  onChange = (e) => {
    const selectedPeople = e.target.selectedPeople.map((person) => ({
      id: person.id,
      displayName: person.displayName,
      personImage: person.personImage,
      email: person?.scoredEmailAddresses?.[0]?.address || person.mail || person?.email,
    }));
    this.setState(
      {
        valid: this.validate(selectedPeople, this.isRequired),
        value: selectedPeople,
        touched: true,
      },
      () => {
        this.updateFormErrors();
        this.handleFieldChange({
          e: {
            target: {
              name: this.fieldName,
              value: JSON.stringify(
                e.target.selectedPeople.map((item) => ({
                  id: item.id,
                  displayName: item.displayName,
                  personImage: item.personImage || "",
                  email: item?.scoredEmailAddresses?.[0]?.address || item.mail || item.email,
                }))
              ),
            },
            value: JSON.stringify(
              e.target.selectedPeople.map((item) => ({
                id: item.id,
                displayName: item.displayName,
                personImage: item.personImage || "",
                email: item?.scoredEmailAddresses?.[0]?.address || item.mail || item.email,
              }))
            ),
          },
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
          eventIndex: this.eventIndex,
        });
        if (this.isStageField) validateStageStep();
      }
    );
  };

  onBlur() {
    this.setState(
      (prevState) => ({
        valid: this.validate(prevState.value, this.isRequired),
        touched: true,
      }),
      () => {
        this.updateFormErrors();
        if (this.isStageField) validateStageStep();
      }
    );
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div>
          <If condition={!this.isPrintPreview && !readOnlyEntry.value}>
            <If condition={this.labelVisible}>
              <Label
                id={this.fieldName + "_label"}
                style={{ fontSize: "14px", fontWeight: "600" }}
              >
                {this.label}
              </Label>
            </If>
            <div className="k-d-flex">
              <If condition={this.isRequired}>
                <div
                  data-testid={this.fieldName + "_required"}
                  style={{
                    width: "5px",
                    backgroundColor: this.state.valid
                      ? "var(--kendo-success-190, inherit)"
                      : "var(--kendo-error-190, inherit)",
                  }}
                />
              </If>
              <div style={{ flexGrow: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CustomPeoplePicker
                    data-testid={this.fieldName}
                    id={this.fieldName}
                    name={this.fieldName}
                    ref={this.inputRef}
                    valid={this.isRequired ? this.state.valid : true}
                    selectedPeople={this.state.value}
                    onBlur={() => this.onBlur()}
                    onChange={(e) => this.onChange(e)}
                    selectionMode={this.selectionMode}
                    disabled={this.isReadOnly || this.state.disabled}
                  />
                  <If
                    condition={
                      this.fieldInformation !== undefined &&
                      this.fieldInformation !== "" &&
                      this.fieldInformation !== "<p></p>"
                    }
                  >
                    <TooltipPopupIcon content={this.fieldInformation} />
                  </If>
                </div>
              </div>
            </div>
            <div className="k-d-flex k-justify-content-between">
              <If condition={this.hint}>
                <Hint>{this.hint}</Hint>
              </If>
            </div>
            <If condition={!this.state.valid && this.state.touched}>
              <Error>{this.validationMessage}</Error>
            </If>
            <Else>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Input
                  id={this.fieldName}
                  name={this.fieldName}
                  label={this.labelVisible ? this.label : null}
                  value={
                    this.state.value?.length > 1
                      ? this.state.value?.map((item) => item.displayName).join(", ")
                      : this.state.value[0]?.displayName
                  }
                  readOnly={this.isPrintPreview}
                  disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
                />
                <If
                  condition={
                    this.fieldInformation !== undefined &&
                    this.fieldInformation !== "" &&
                    this.fieldInformation !== "<p></p>"
                  }
                >
                  <TooltipPopupIcon content={this.fieldInformation} />
                </If>
              </div>
            </Else>
          </If>
        </div>
      </Ripple>
    );
  }
}
